/* eslint-disable no-console */
// @ts-check

/**
 * @typedef {import('./holiday24/helpers/svgMapHelper').ViewBox} ViewBox
 * @typedef {import('./holiday24/helpers/svgMapHelper').CountryTermModel} CountryTermModel
 */

import { ready } from './holiday24/utils';
import { svgMapHelper } from './holiday24//helpers/svgMapHelper';
import { searchFormHelper } from './holiday24/helpers/searchFormHelper';

const accommodationFrontpageLocation = () => {
  const main = /** @type{HTMLDivElement} */ (document.querySelector('.accommodation-frontpage-location'));
  const mainGeo = /** @type{HTMLDivElement} */ (document.querySelector('.accommodation-frontpage-geography'));
  const map = /** @type{HTMLDivElement} */ (main.querySelector('.accommodation-frontpage-location__map'));
  const svg = /** @type{SVGAElement} */ (main.querySelector('.accommodation-frontpage-location__svg'));
  const showAllBtn = /** @type{HTMLButtonElement} */ (main.querySelector('.accommondation-frontpage-map__buttons .showall'));

  const terms = /** @type{CountryTermModel[]} */ (window['accommodationFrontpageLocationData'].terms);
  const allViewBox = window['accommodationFrontpageLocationData'].allViewBox;
  let activeCountryIsoCode = window['accommodationFrontpageLocationData'].initActiveCountry;
  let isExpanded = main.classList.contains('is-expanded');
  let userHasShrinkedMap = false;
  let savedActiveCountryIsoCode = activeCountryIsoCode;

  const { updateCountryId, updateGeography, countrySelect, geographyInput } = searchFormHelper();
  const { animateViewBox, addMarkers, recalculateMarkerPositions, removeMarkers, setMarkerActive, onMarkerClick } = svgMapHelper(svg, map, terms);

  addMarkers(activeCountryIsoCode, isExpanded);

  /**
   * Updates CountryId by CountryIsoCode.
   * @param {string} countryIsoCode
   */
  const updateCountryIdByCountryIsoCode = (countryIsoCode) => {
    updateCountryId(countryIsoCode === '' ? '' : terms.find(t => t.countryIsoCode.toLowerCase() === countryIsoCode.toLowerCase()).countryId);
  };

  /**
   * @param {string} countryIsoCode
   * @return {Promise<void>} Await animation is done.
   */
  const setActiveCountry = async (countryIsoCode) => {
    return new Promise((resolve) => {
      if (activeCountryIsoCode === countryIsoCode) {
        resolve();
        return;
      }

      removeMarkers();
      savedActiveCountryIsoCode = activeCountryIsoCode;
      activeCountryIsoCode = countryIsoCode;

      const isShowingAll = activeCountryIsoCode === 'all';

      const countryViewBox = isShowingAll
        ? allViewBox
        : terms.find(tt => tt.countryIsoCode.toLowerCase() === activeCountryIsoCode.toLowerCase()).countryViewBox;

      animateViewBox(countryViewBox, 500, () => {
        addMarkers(activeCountryIsoCode, isExpanded);
        resolve();
      });

      if (showAllBtn != null) {
        showAllBtn.classList.toggle('is-showingall', isShowingAll);
      }

      svg.querySelectorAll('.land--activatable').forEach(l => l.classList.remove('is-active'));
      if (activeCountryIsoCode === 'all') {
        svg.querySelectorAll('.land--activatable').forEach(l => l.classList.add('is-active'));
      } else {
        document.getElementById(activeCountryIsoCode.toUpperCase()).classList.add('is-active');
      }

      mainGeo.querySelectorAll('.geography').forEach(l => l.classList.remove('is-active', 'show-title'));
      mainGeo.querySelectorAll('.geoterm').forEach(l => l.classList.remove('is-active'));
      if (activeCountryIsoCode === 'all') {
        mainGeo.querySelectorAll('.geography').forEach(l => l.classList.add('is-active', 'show-title'));
      } else {
        mainGeo.querySelector(`.geography--${activeCountryIsoCode}`)?.classList.add('is-active');
      }
    });
  };

  /**
   * Sets geography is-active in the list with all geographies
   * @param {string} geographyId
   */
  const setGeotermActive = (geographyId) => {
    mainGeo.querySelectorAll('.geoterm').forEach(t => t.classList.remove('is-active'));
    mainGeo.querySelector(`.geoterm[data-geography-id="${geographyId}"]`)?.classList.add('is-active');
  };

  /**
   * Expands the map
   * @param {boolean} doAddMarkers
   * @param {boolean} forceExpand If true, the map will expand even if the user has shrinked it manually}
   * @param {string} teLabel Optional label for tracking event
   */
  const expandMap = (doAddMarkers, forceExpand, teLabel) => {
    if (isExpanded) {
      return;
    }

    if (userHasShrinkedMap && !forceExpand) {
      return;
    }

    // Track event
    window['trackEvent']('AccommodationFrontpageLocation', 'expandMap', teLabel);

    isExpanded = true;
    if (doAddMarkers) {
      main.addEventListener('transitionend', function handleTransitionEnd(e) {
        if (e.propertyName === 'width') {
          setTimeout(() => {
            addMarkers(activeCountryIsoCode, isExpanded);
          }, 50);
          main.removeEventListener('transitionend', handleTransitionEnd);
        }
      });
    }
    main.classList.add('is-expanded');
  };

  /**
   * Shrinks the map
   * @param {string} teLabel Optional label for tracking event
   */
  const shrinkMap = (teLabel) => {
    // Track event
    window['trackEvent']('AccommodationFrontpageLocation', 'shrinkMap', teLabel);

    userHasShrinkedMap = true;
    isExpanded = false;
    removeMarkers();
    main.classList.remove('is-expanded');
  };

  main.querySelectorAll('.land--activatable').forEach((l) => {
    l.addEventListener('click', (event) => {
      const target = /** @type {SVGPathElement} */(event.target);
      updateCountryIdByCountryIsoCode(target.id);
      window['trackEvent']('AccommodationFrontpageLocation', 'click', `land:${target.getAttribute('title')}`);
    });
  });

  // When search is updated, accommodationSearchUpdated is triggered and we move to new position
  document.body.addEventListener('accommodationSearchUpdated', async (/** @type{CustomEvent} */e) => {
    let geography = null;
    if (e.detail.Adjustment != null) {
      geography = e.detail.Adjustment.Geography;
    } else if (e.detail.Search != null) {
      geography = e.detail.Search.Geography;
    }

    if (geography === null) {
      return;
    }

    const countryId = geography.GeographyCountryId; // GUID
    const geographyId = geography.Geographies.map(d => d.Id).filter(_ => _)[0]; // GUID - remove empty and take the first

    if (countryId != null && countryId !== '') {
      await setActiveCountry(terms.find(t => t.countryId === countryId).countryIsoCode);
    } else {
      setActiveCountry('all');
    }

    setMarkerActive(geographyId, countryId);
    setGeotermActive(geographyId);
  });

  if (showAllBtn != null) {
    showAllBtn.addEventListener('click', () => {
      const isShowingAll = showAllBtn.classList.contains('is-showingall');
      updateCountryIdByCountryIsoCode(isShowingAll ? savedActiveCountryIsoCode : '');
      window['trackEvent']('AccommodationFrontpageLocation', 'click', `showall:${isShowingAll ? 'plus' : 'minus'}`);
    });
  }

  main.querySelector('.btn--expand').addEventListener('click', () => expandMap(true, true, 'button'));
  main.querySelector('.btn--shrink').addEventListener('click', () => shrinkMap('button'));
  if (countrySelect != null) {
    countrySelect.addEventListener('change', () => expandMap(false, false, 'country'));
  }
  geographyInput.addEventListener('focus', () => expandMap(true, false, 'geography'));

  mainGeo.querySelectorAll('.geoterm').forEach((t) => {
    t.addEventListener('click', (e) => {
      const target = /** @type {HTMLDivElement} */(e.target);
      target.classList.add('is-active');
      updateGeography(target.dataset.countryId, target.dataset.geographyId, target.dataset.title);
      window['trackEvent']('AccommodationFrontpageLocation', 'click', `link:${target.dataset.title}`);
    });
  });

  onMarkerClick((payload) => {
    updateGeography(payload.countryId, payload.geographyId, payload.title);
    window['trackEvent']('AccommodationFrontpageLocation', 'click', `marker:${payload.title}`);
  });

  var mql1 = window.matchMedia('(min-width: 1200px)');
  mql1.addEventListener('change', recalculateMarkerPositions);
  var mql2 = window.matchMedia('(min-width: 992px)');
  mql2.addEventListener('change', (payload) => {
    if (payload.matches) {
      removeMarkers();
      addMarkers(activeCountryIsoCode, isExpanded);
    }
  });
};

ready(() => {
  accommodationFrontpageLocation();
});
